import React from "react";
import Image from "next/image";
import Head from "next/head";
import Link from "next/link";
import CtaOne from "@components/CTA/CtaOne";
import FAQ from "@components/FAQ";
import Legal from "components/SignatureLtd/Legal";
import { BsPlusCircleFill } from "react-icons/bs";
import { BASE_ASSETS_URL } from "../../../constants/constants";
import { createUTMQueryParams } from "@utils/signature_owner";
import { FaMinusCircle } from "react-icons/fa";
import FaqCard from "@components/Cards/FaqCard";

const bgColors = [
  "bg-green-100",
  "bg-yellow-100",
  "bg-red-100",
  "bg-gray-100",
  "bg-blue-100",
  "bg-purple-100",
  "bg-indigo-100",
];

const gettingStarted = [
  {
    title: "Install the app",
    data: [
      "Search for “BoloSign” in the Play Store/Apple Store.",
      "Install the app.",
    ],
  },
  {
    title: "Upload the Document",
    data: [
      "Upload the document that you want to send for signatures.",
      "Add the Signers.",
    ],
  },
  {
    title: "Send for Signature",
    data: ["Add the necessary fields.", "Send the document for signature."],
  },
];

const features = [
  {
    image: "action-1.png",
    title: "Drag and Drop Fields",
    description: `Click the <div class="w-fit h-fit px-2 inline-block bg-gradient-to-r from-violet-500 to-blue-500 text-white rounded-full font-sans">+</div> Fields button and place signatures, initials, stamps, text, and more exactly where needed.`,
  },
  {
    image: "action-2.png",
    title: "Send Reminders to Signers",
    description: `With just one tap, send reminders to signers and keep your workflow on track.
    <ul class="mt-2 ml-6 list-disc">
      <li>Boost Completion Rates</li>
      <li>Reduce Delays</li>
      <li>Stay Updated with Real-Time Notifications</li>
    </ul>
    `,
  },
  {
    image: "action-3.png",
    title: "Select Who Needs to Sign",
    description: `Choose how you want to collect signatures:
    <ul class="mt-2 ml-6 list-disc">
      <li>Sign It Yourself – Instantly sign your document</li>
      <li>Send for Signature – Invite others via email</li>
      <li>In-Person Signing – Get signatures on the spot</li>
    </ul>`,
  },
  {
    image: "action-4.png",
    title: "Sign Anytime, Anywhere",
    description: `Generate Signing links, send the document, and get it signed anytime, anywhere without any delay.`,
  },
  {
    image: "action-5.png",
    title: "Certificate of Completion",
    description: `To ensure the signed document's legality and security, the signers can download a certificate with the timestamps and other details. Meet Global eSignature Standards (ESIGN, UETA, etc.)`,
  },
  {
    image: "action-6.png",
    title: "Wide Ranged Integrations",
    description: `Upload and save your PDFs from various players like Dropbox, Google Drive, Microsoft OneDrive, Box etc`,
  },
  {
    image: "action-7.png",
    title: "Guides and Resources",
    description: `Learn the best practices for using BoloSign to streamline your document signing process.<br><a class="underline" href="https://help.boloforms.com/en/collections/15429283-how-do-you-use-the-bolosign-mobile-app-iphone-android" target="_blank">Explore More Guides</a>`,
  },
  {
    image: "action-8.png",
    title: "PDF Templates",
    description: `Save time by reusing pre-saved templates for frequently used documents. Customize roles and send them to different people in just a few clicks.`,
  },
  {
    image: "action-9.png",
    title: "Assign to Me",
    description: `Need to sign a document yourself? Use the "Assign to Me" feature to quickly add your signature.
    <ul class="text-left mt-2">
      <li>✔ One-Tap Self-Signing</li>
      <li>✔ Fill & Sign Without Extra Steps</li>
      <li>✔ Save & Send Instantly</li>
    </ul>`,
  },
];

const reviews = [
  {
    rating: 5,
    name: "Lisa Carter",
    designation: "Founder",
    review: `“Signing contracts on my phone has never been easier! BoloSign is fast, simple, and keeps everything organized. Total lifesaver!”`,
    image: "lisa.jpg",
  },
  {
    rating: 5,
    name: "Sarah Thompson",
    designation: "CEO",
    review: `“I used to struggle with getting clients to sign documents on time. With BoloSign, it’s instant—no printing, no scanning, just tap and sign!”`,
    image: "sarah.jpg",
  },
  {
    rating: 5,
    name: "James Reynolds",
    designation: "Operations Manager",
    review: `“e handle dozens of agreements daily, and BoloSign makes it effortless. Automated reminders and audit trails keep us compliant without the hassle”`,
    image: "james.jpg",
  },
];

const comparisonsData = [
  {
    feature: "Pricing",
    BoloSign: "60$/Year",
    SignNow: "100$/Year",
    SignEasy: "140$/Year",
    DocuSign: "150$/Year",
    PandaDoc: "120$/Year",
  },
  {
    feature: "Forms",
    BoloSign: "Unlimited Forms",
    SignNow: false,
    SignEasy: false,
    DocuSign: false,
    PandaDoc: false,
  },
  {
    feature: "PDFs",
    BoloSign: "Unlimited PDFs",
    SignNow: "Limited",
    SignEasy: "Limited",
    DocuSign: "Limited",
    PandaDoc: "Limited",
  },
  {
    feature: "PDF Templates",
    BoloSign: "Unlimited PDF Templates",
    SignNow: false,
    SignEasy: false,
    DocuSign: false,
    PandaDoc: false,
  },
  {
    feature: "Collaborators",
    BoloSign: "Unlimited Collaborators",
    SignNow: "Limited",
    SignEasy: "Limited",
    DocuSign: "Limited",
    PandaDoc: "Limited",
  },
];

const faqs = [
  {
    faqQuestion: "Is BoloSign legally binding?",
    faqAnswer:
      "Yes, BoloSign complies with e-signature laws and regulations, ensuring your documents are legally binding. We are compliant with all the norms of UETA, ISO, HIPAA, ETA, etc.",
  },
  {
    faqQuestion: "How do I place my signature on the document?",
    faqAnswer:
      "You can either draw your signature directly on your screen or upload a pre-saved image of your signature.",
  },
  {
    faqQuestion: "What file formats does BoloSign support?",
    faqAnswer: "BoloSign supports a wide range of file formats, including PDF.",
  },
  {
    faqQuestion:
      "If I buy a BoloSign subscription through the mobile app, can I still use the web version?",
    faqAnswer: `Yes, you can access your subscription features across any device and platform. Simply log in to <a class='underline' href='https://signature.boloforms.com/login?from=MOBILE_APP_PAGE${createUTMQueryParams(
      { from: "MOBILE_APP_PAGE" },
    )}'>https://www.boloforms.com/</a> with your username and password created on the app to access all the same great features.`,
  },
  {
    faqQuestion:
      "On what devices does the BoloSign mobile eSignature app work?",
    faqAnswer:
      "The app is currently supported only on Android devices; the iOS version also will be released soon.",
  },
];

const MobileNewPage = () => {
  const brands = ["BoloSign", "SignNow", "SignEasy", "DocuSign", "PandaDoc"];
  return (
    <div className="w-full relative overflow-hidden font-inter">
      <Head>
        <title>BoloSign App - AI for Smart Contract Management</title>
        <meta
          name="description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign " />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/mobile/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign App - AI for Smart Contract Management"
        />
        <meta
          property="og:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/mobile/"
        />
        <meta
          name="twitter:title"
          content="BoloSign App - AI for Smart Contract Management"
        />
        <meta
          name="twitter:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/mobile/"
        />
      </Head>

      {/* landing page  */}
      <div className="w-full bg-gradient-to-tr bg-indigo-600 pt-16 sm:pt-20 md:pt-32">
        <section className="w-full max-w-screen-xl px-4 lg:px-8 mx-auto flex flex-col sm:flex-row justify-between items-center sm:items-start md:items-center gap-12">
          {/* <Image
          src={`${BASE_ASSETS_URL}/v2/mobile-app-page/bolosign-faded-logo.png`}
          className="w-[250px] sm:w-[300px] lg:w-[500px] h-auto absolute top-0 -right-20 -z-10"
          width={100}
          height={100}
          loading="lazy"
          alt=""
        /> */}
          <div className="w-full sm:w-1/2 text-left flex flex-col gap-4 sm:gap-7 xl:gap-8">
            <div className="text-4xl md:text-5xl xl:text-7xl font-bold text-white">
              <p>{`BoloSign -`}</p>
              <p className="mt-1">eSign App</p>
            </div>

            <div className="text-xl md:text-2xl xl:text-3xl text-white">
              <p>{`Sign Documents Anytime, Anywhere – Fast, Secure, & Legally Binding`}</p>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-wrap mt-4 sm:mt-0 md:mt-4">
              <Link
                href={`https://apps.apple.com/app/bolosign-upload-sign-send/id6717583531`}
              >
                <Image
                  // src={`${BASE_ASSETS_URL}/v2/footer/apple-store.png`}
                  src={require(`assets/v2/footer/apple-store.png`)}
                  className="h-12 md:h-14 w-auto"
                  width={100}
                  height={100}
                  loading="lazy"
                  alt=""
                />
              </Link>
              <Link
                href={`https://play.google.com/store/apps/details?id=com.boloforms.signature`}
              >
                <Image
                  // src={`${BASE_ASSETS_URL}/v2/footer/play-store.png`}
                  src={require(`assets/v2/footer/play-store.png`)}
                  className="h-12 md:h-14 w-auto"
                  width={100}
                  height={100}
                  loading="lazy"
                  alt=""
                />
              </Link>
            </div>
          </div>
          <div className="sm:w-1/2 bg-cream rounded-t-xl sm:rounded-t-3xl px-4 pt-12 pb-0 border border-zinc-300">
            <Image
              // src={`${BASE_ASSETS_URL}/v2/mobile-app-page/landing-page.png`}
              src={require(`assets/v2/mobile-app-page/landing-page.png`)}
              className="w-full h-auto"
              width={100}
              height={100}
              loading="lazy"
              alt=""
            />
          </div>
        </section>
      </div>

      {/* getting started  */}
      <section className="w-full max-w-screen-xl mx-auto px-4 lg:px-8 flex flex-col gap-10 mt-20 sm:mt-32">
        <h2 className="max-w-screen-md font-bold leading-[45px] sm:leading-[55px] lg:leading-[70px] text-[36px] sm:text-[48px] lg:text-[60px]">
          Getting Started in 3 Simple Steps
        </h2>
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {gettingStarted?.map((item, index) => {
            return (
              <div
                key={index}
                className={`w-full group flex flex-col px-5 py-7 sm:p-7 gap-3 border border-gray-300 rounded-xl transition-all duration-300 ease-in-out bg-cream`}
              >
                <div className="w-full flex items-center gap-2">
                  <div className="w-[60px] h-[60px] bg-white rounded-full grid place-items-center">
                    0{index + 1}
                  </div>
                  <h1 className="flex-1 text-gray-800 text-xl lg:text-2xl font-bold">
                    {item?.title}
                  </h1>
                </div>
                <ul className="font-normal text-base sm:text-lg text-gray-800 list-disc ml-6">
                  {item?.data?.map((val, index) => {
                    return (
                      <li key={index} className="">
                        {val}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* action  */}
      <section className="w-full max-w-screen-xl mx-auto flex flex-col justify-center items-center sm:gap-12 px-4 lg:px-8 mt-20 sm:mt-32">
        <div className="max-w-screen-md mx-auto text-center">
          <CtaOne heading2={"See BoloSign Mobile App in action"} />
        </div>
        <div className="w-full flex flex-col gap-12 sm:gap-20">
          <div className="w-full flex flex-col gap-16">
            {features?.map((feature, index) => (
              <div
                key={index}
                className={`w-full flex flex-col justify-between items-center gap-4 sm:gap-20 ${
                  index % 2 === 0 ? "sm:flex-row-reverse" : "sm:flex-row"
                }`}
              >
                <div className="w-full flex flex-col gap-1 sm:gap-6">
                  <h1 className="text-2xl sm:text-4xl lg:text-5xl font-semibold">
                    {feature.title}
                  </h1>
                  <p
                    className="text-lg sm:text-xl lg:text-2xl"
                    dangerouslySetInnerHTML={{ __html: feature.description }}
                  ></p>
                </div>
                <div className="w-full">
                  <Image
                    // src={`${BASE_ASSETS_URL}/v2/mobile-app-page/${feature.image}`}
                    src={require(`@assets/mobile-app-page/${feature.image}`)}
                    className="w-full h-auto border border-gray-300 rounded-[30px]"
                    width={10}
                    height={10}
                    alt=""
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* comparison  */}
      <section
        className="bg-[#0d0d0d] my-24 md:my-32 py-16 md:py-32"
        id="reviews"
      >
        <div className="px-4 md:px-8 max-w-6xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-cream text-center mb-8 sm:mb-16">
            {`Here’s Why BoloSign Is Better To Other Alternatives`}
          </h1>

          <div className="overflow-x-auto text-white bg-black rounded-xl no-scrollbar">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">
                    Features
                  </th>
                  {brands.map((brand) => (
                    <th
                      key={brand}
                      className={`px-4 py-4 text-center font-semibold ${
                        brand === "BoloSign"
                          ? "bg-zinc-900 text-white font-bold theme-text-color"
                          : ""
                      }`}
                    >
                      {brand}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonsData.map((item, index) => (
                  <tr key={index} className="border-t border-zinc-800">
                    <td className="px-4 py-3">{item.feature}</td>
                    {brands.map((brand) => (
                      <td
                        key={brand}
                        className={`px-4 py-4 text-center ${
                          brand === "BoloSign"
                            ? "bg-zinc-900 font-semibold"
                            : ""
                        }`}
                      >
                        {item[brand] === false ? (
                          <span className="text-zinc-300">—</span>
                        ) : item[brand] === "Limited" ? (
                          <span className="text-zinc-300">Limited</span>
                        ) : (
                          <span className="text-zinc-300">{item[brand]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white my-12 sm:my-24 md:my-32" id="reviews">
        <div className="px-4 md:px-8 max-w-screen-xl mx-auto">
          <h1 className="text-4xl sm:text-6xl font-bold text-black text-center">
            Join 1,000+ Users...
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-y-10 sm:gap-x-6 mt-12 sm:mt-16">
            {reviews.map((review, index) => (
              <div key={index} className="w-full relative flex flex-col">
                <div className="w-full pl-[120px] pb-2">
                  <Image
                    width={10}
                    height={2}
                    // src={require(`@assets/mobile-app-page/stars.png`)}
                    src={`${BASE_ASSETS_URL}/v2/mobile-app-page/stars.png`}
                    alt="Stars"
                    className="h-10 w-auto z-20"
                  />
                </div>

                <div className="h-full flex flex-col gap-2 bg-cream rounded-2xl px-4 py-6 text-gray-900">
                  <Image
                    src={require(`@assets/mobile-app-page/${review.image}`)}
                    // src={`${BASE_ASSETS_URL}/v/mobile-app-page/${review.image}`}
                    width={20}
                    height={20}
                    alt="User"
                    className="w-[120px] h-[120px] object-cover absolute rounded-full left-0 -top-[16px] border-[5px] border-white"
                  />
                  <p className="text-lg md:text-xl font-semibold pl-[110px]">
                    {review.name}, {review.designation}
                  </p>
                  <div className="text-center">
                    <p className="text-lg">{review.review}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* legal  */}
      <section className="w-full max-w-screen-xl mx-auto px-4 lg:px-8 mt-20 sm:mt-32">
        <Legal
          bgColor="bg-transparent"
          shadow={false}
          complianceGroupImage={false}
        />
      </section>

      {/* cta  */}
      <section className="w-full px-4 lg:px-8 mt-20 md:mt-32 bg-gradient-clm py-12 sm:py-20">
        <h1 className="max-w-screen-lg text-white mx-auto text-center text-2xl sm:text-3xl md:text-4xl font-semibold">
          {`Running a business is busy enough— sign documents the easy way with BoloSign!`}
        </h1>
        <div className="flex justify-center items-center gap-2 sm:gap-4 flex-wrap mt-8">
          <Link
            href={`https://apps.apple.com/app/bolosign-upload-sign-send/id6717583531`}
          >
            <Image
              // src={`${BASE_ASSETS_URL}/v2/footer/apple-store.png`}
              src={require(`assets/v2/footer/apple-store.png`)}
              className="h-12 sm:h-14 md:h-16 w-auto"
              width={100}
              height={100}
              loading="lazy"
              alt=""
            />
          </Link>
          <Link
            href={`https://play.google.com/store/apps/details?id=com.boloforms.signature`}
          >
            <Image
              // src={`${BASE_ASSETS_URL}/v2/footer/play-store.png`}
              src={require(`assets/v2/footer/play-store.png`)}
              className="h-12 sm:h-14 md:h-16 w-auto"
              width={100}
              height={100}
              loading="lazy"
              alt=""
            />
          </Link>
        </div>
      </section>

      {/* faq  */}
      <section className="w-full max-w-screen-lg mx-auto flex flex-col justify-center items-center sm:gap-2 mt-20 sm:mt-32">
        <div className="text-left px-4 lg:px-8">
          <CtaOne heading2="Frequently Asked Questions" />
        </div>
        <div className="w-full max-w-[1000px] mx-auto flex flex-col gap-4 sm:gap-8">
          {faqs.map((faq, index) => (
            <FaqCard
              key={index}
              faqQuestion={faq.faqQuestion}
              faqAnswer={faq.faqAnswer}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default MobileNewPage;
